import { CalendarEvent, CalendarTask, CalendarContact } from "@/types/calendar.types";
import {
  DEMO_CONTRACT_IDS,
  DEMO_USER_IDS,
  demoProfilePictures,
} from "@/mockData/demoContractData";

function getRelativeDate(daysOffset: number): string {
  const date = new Date();
  date.setDate(date.getDate() + daysOffset);
  return date.toISOString().split("T")[0];
}

export const calendarContacts: CalendarContact[] = [
  {
    id: "1",
    name: "Adam Silver",
    role: "Entertainment Lawyer",
    company: "Silver & Associates",
    email: "adam@silverlaw.com",
    phone: "+1 (555) 123-4567",
    avatar: demoProfilePictures[DEMO_USER_IDS.ADAM_SILVER],
    tags: ["Legal", "Contracts", "IP Rights"],
    lastContact: "2 days ago",
    isStarred: true,
  },
  {
    id: "2",
    name: "Roger Goodell",
    role: "Talent Manager",
    company: "Goodell Management",
    email: "roger@goodellmgmt.com",
    phone: "+1 (555) 234-5678",
    avatar: demoProfilePictures[DEMO_USER_IDS.ROGER_GOODELL],
    tags: ["Management", "Deals", "Strategy"],
    lastContact: "1 week ago",
    isStarred: false,
  },
  {
    id: "3",
    name: "Sarah Chen",
    role: "Music Producer",
    company: "Chen Studios",
    email: "sarah@chenstudios.com",
    phone: "+1 (555) 345-6789",
    avatar: undefined,
    tags: ["Production", "Recording", "Collaboration"],
    lastContact: "3 days ago",
    isStarred: true,
  },
  {
    id: "4",
    name: "Marcus Johnson",
    role: "Accountant",
    company: "Johnson Financial",
    email: "marcus@johnsonfinancial.com",
    phone: "+1 (555) 456-7890",
    avatar: undefined,
    tags: ["Accounting", "Finance", "Tax"],
    lastContact: "1 month ago",
    isStarred: false,
  },
  {
    id: "5",
    name: "Emily Rodriguez",
    role: "Booking Agent",
    company: "Live Nation",
    email: "emily@livenation.com",
    phone: "+1 (555) 567-8901",
    avatar: undefined,
    tags: ["Agency", "Touring", "Events"],
    lastContact: "5 days ago",
    isStarred: false,
  },
  {
    id: "6",
    name: "David Kim",
    role: "A&R Director",
    company: "Sony Music",
    email: "david@sonymusic.com",
    phone: "+1 (555) 678-9012",
    avatar: undefined,
    tags: ["Label", "A&R", "Recording"],
    lastContact: "2 weeks ago",
    isStarred: true,
  },
];

export const calendarTasks: CalendarTask[] = [
  {
    id: "task_1",
    title: "Review Recording Deal amendments",
    description: "Adam Silver sent updated terms for the Artist Recording Deal",
    dueDate: getRelativeDate(0),
    dueTime: "17:00",
    completed: false,
    priority: "high",
    contractId: DEMO_CONTRACT_IDS.ARTIST_RECORDING_DEAL,
    contactId: "1",
    type: "review",
  },
  {
    id: "task_2",
    title: "Sign Producer Agreement",
    description: "Final signature required for the Producer Agreement",
    dueDate: getRelativeDate(1),
    dueTime: "12:00",
    completed: false,
    priority: "high",
    contractId: DEMO_CONTRACT_IDS.PRODUCER_AGREEMENT,
    contactId: "2",
    type: "signature",
  },
  {
    id: "task_3",
    title: "Follow up with Sony A&R",
    description: "Check on the status of the new recording opportunity",
    dueDate: getRelativeDate(0),
    completed: false,
    priority: "medium",
    contactId: "6",
    type: "follow-up",
  },
  {
    id: "task_4",
    title: "Send Brand Partnership counter-offer",
    description: "Prepare and send counter-offer for NIL deal",
    dueDate: getRelativeDate(2),
    completed: false,
    priority: "medium",
    contractId: DEMO_CONTRACT_IDS.BRAND_PARTNERSHIP_NIL,
    type: "general",
  },
  {
    id: "task_5",
    title: "Review SAFE Agreement terms",
    description: "Complete analysis of investment terms",
    dueDate: getRelativeDate(2),
    completed: true,
    priority: "low",
    contractId: DEMO_CONTRACT_IDS.SAFE_AGREEMENT,
    type: "review",
  },
  {
    id: "task_6",
    title: "Schedule call with booking agent",
    description: "Discuss upcoming tour opportunities with Emily",
    dueDate: getRelativeDate(3),
    completed: false,
    priority: "low",
    contactId: "5",
    type: "follow-up",
  },
  {
    id: "task_7",
    title: "Q1 royalty payment verification",
    description: "Verify that Q1 royalty payments have been processed correctly",
    dueDate: getRelativeDate(5),
    completed: false,
    priority: "medium",
    contactId: "4",
    type: "payment",
  },
];

export const calendarEvents: CalendarEvent[] = [
  {
    id: "event_artist_recording_review",
    title: "Artist Recording Deal Review",
    date: getRelativeDate(0),
    startTime: "10:00",
    endTime: "11:30",
    timezone: "EST",
    participants: ["1", "demo_user"],
    description:
      "Review the Artist Recording Deal terms including $420K advance, recoupment structure, and backend royalty rates. Discussion on Initial Term and Post-Term Control provisions.",
    type: "contract",
    contractId: DEMO_CONTRACT_IDS.ARTIST_RECORDING_DEAL,
    contactId: "1",
    meetingLink: "https://meet.google.com/abc-defg-hij",
  },
  {
    id: "event_producer_deadline",
    title: "Producer Agreement Signing Deadline",
    date: getRelativeDate(1),
    startTime: "17:00",
    endTime: "17:30",
    timezone: "EST",
    participants: ["2"],
    description:
      "Final deadline to sign the Producer Agreement. All terms have been negotiated and approved.",
    type: "deadline",
    contractId: DEMO_CONTRACT_IDS.PRODUCER_AGREEMENT,
    contactId: "2",
  },
  {
    id: "event_brand_partnership_meeting",
    title: "Brand Partnership Discussion",
    date: getRelativeDate(0),
    startTime: "14:00",
    endTime: "15:30",
    timezone: "EST",
    participants: ["1", "2", "demo_user"],
    description:
      "Discussion of NIL Brand Partnership opportunity. Review of exclusivity clauses, compensation structure, and social media requirements.",
    type: "meeting",
    contractId: DEMO_CONTRACT_IDS.BRAND_PARTNERSHIP_NIL,
    meetingLink: "https://zoom.us/j/123456789",
  },
  {
    id: "event_safe_review",
    title: "SAFE Agreement Terms Review",
    date: getRelativeDate(2),
    startTime: "09:00",
    endTime: "10:00",
    timezone: "EST",
    participants: ["demo_user", "4"],
    description:
      "Review SAFE Agreement investment terms, valuation cap, and conversion mechanics.",
    type: "review",
    contractId: DEMO_CONTRACT_IDS.SAFE_AGREEMENT,
    contactId: "4",
  },
  {
    id: "event_weekly_standup",
    title: "Weekly Deal Team Sync",
    date: getRelativeDate(0),
    startTime: "09:00",
    endTime: "09:30",
    timezone: "EST",
    participants: ["1", "2"],
    description:
      "Weekly sync on ongoing contract negotiations and pending signatures.",
    type: "meeting",
    meetingLink: "https://meet.google.com/xyz-uvwx-yz",
  },
  {
    id: "event_legal_review",
    title: "Legal Team Review",
    date: getRelativeDate(1),
    startTime: "11:00",
    endTime: "12:30",
    timezone: "EST",
    participants: ["1", "demo_user"],
    description:
      "Comprehensive legal review of all pending contracts with Adam Silver.",
    type: "review",
    contactId: "1",
    meetingLink: "https://meet.google.com/legal-review",
  },
  {
    id: "event_producer_call",
    title: "Studio Session Planning",
    date: getRelativeDate(3),
    startTime: "13:00",
    endTime: "14:00",
    timezone: "EST",
    participants: ["3"],
    description:
      "Plan upcoming studio sessions with Sarah Chen for new recordings.",
    type: "meeting",
    contactId: "3",
    meetingLink: "https://meet.google.com/studio-plan",
  },
  {
    id: "event_quarterly_review",
    title: "Q1 Contract Portfolio Review",
    date: getRelativeDate(4),
    startTime: "10:00",
    endTime: "12:00",
    timezone: "EST",
    participants: ["1", "2", "4", "demo_user"],
    description:
      "Quarterly review of all active contracts, upcoming renewals, and negotiation priorities.",
    type: "review",
    meetingLink: "https://zoom.us/j/quarterly-review",
  },
  {
    id: "event_booking_call",
    title: "Tour Booking Strategy",
    date: getRelativeDate(3),
    startTime: "15:00",
    endTime: "16:00",
    timezone: "EST",
    participants: ["5"],
    description:
      "Discuss summer tour opportunities and venue negotiations with Emily Rodriguez.",
    type: "meeting",
    contactId: "5",
    meetingLink: "https://meet.google.com/tour-booking",
  },
  {
    id: "event_ar_meeting",
    title: "Sony A&R Check-in",
    date: getRelativeDate(5),
    startTime: "14:00",
    endTime: "15:00",
    timezone: "EST",
    participants: ["6"],
    description:
      "Check in with David Kim on new recording opportunities and label support.",
    type: "meeting",
    contactId: "6",
    meetingLink: "https://meet.google.com/sony-ar",
  },
  {
    id: "event_renewal_discussion",
    title: "Recording Deal Renewal Discussion",
    date: getRelativeDate(6),
    startTime: "11:00",
    endTime: "12:00",
    timezone: "EST",
    participants: ["1", "demo_user"],
    description:
      "Early discussion on potential renewal terms for the Artist Recording Deal ahead of the initial term expiration.",
    type: "contract",
    contractId: DEMO_CONTRACT_IDS.ARTIST_RECORDING_DEAL,
    contactId: "1",
    meetingLink: "https://meet.google.com/renewal-disc",
  },
  {
    id: "event_quick_sync",
    title: "Quick Sync - Roger",
    date: getRelativeDate(0),
    startTime: "16:00",
    endTime: "16:15",
    timezone: "EST",
    participants: ["2"],
    description: "Brief check-in on Producer Agreement status.",
    type: "meeting",
    contactId: "2",
  },
  {
    id: "event_signature_reminder",
    title: "Signature Follow-up",
    date: getRelativeDate(2),
    startTime: "14:30",
    endTime: "14:45",
    timezone: "EST",
    participants: ["1"],
    description: "Follow-up call regarding pending signatures with Adam Silver.",
    type: "deadline",
    contactId: "1",
  },
];

export function getWeekDays(startDate: Date = new Date()): Date[] {
  const dayOfWeek = startDate.getDay();
  const startOfWeek = new Date(startDate);
  startOfWeek.setDate(startDate.getDate() - dayOfWeek);

  return Array.from({ length: 7 }, (_, i) => {
    const date = new Date(startOfWeek);
    date.setDate(startOfWeek.getDate() + i);
    return date;
  });
}

export function formatDateString(date: Date): string {
  return date.toISOString().split("T")[0];
}

export function getEventsForWeek(weekDays: Date[]): Record<string, CalendarEvent[]> {
  const eventsByDay: Record<string, CalendarEvent[]> = {};

  weekDays.forEach((day) => {
    const dayStr = formatDateString(day);
    eventsByDay[dayStr] = calendarEvents.filter((e) => e.date === dayStr);
  });

  return eventsByDay;
}

export function getUpcomingEvents(limit: number = 5): CalendarEvent[] {
  const today = formatDateString(new Date());
  const weekFromNow = formatDateString(new Date(Date.now() + 7 * 24 * 60 * 60 * 1000));

  return calendarEvents
    .filter((e) => e.date >= today && e.date <= weekFromNow)
    .sort((a, b) => {
      if (a.date !== b.date) return a.date.localeCompare(b.date);
      return a.startTime.localeCompare(b.startTime);
    })
    .slice(0, limit);
}

export function getContactById(id: string): CalendarContact | undefined {
  return calendarContacts.find((c) => c.id === id);
}

export function getContactName(id: string): string {
  if (id === "demo_user") return "You";
  const contact = getContactById(id);
  return contact?.name || id;
}
